"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, Eye, EyeOff, Mail, Key } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useTheme } from "next-themes";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { useUser } from "@/hooks/useUser";
import { useAuth } from "@/lib/auth-client";
import { Button } from "@/lib/components/button";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/lib/components/form";
import { Input } from "@/lib/components/input";
import { toast } from "@/lib/components/use-toast";
import { useAppConfig } from "@/lib/config";

function GoogleIcon({ className }: { className?: string }) {
	return (
		<svg
			className={className}
			viewBox="0 0 24 24"
			xmlns="http://www.w3.org/2000/svg"
		>
			<path
				fill="#4285F4"
				d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
			/>
			<path
				fill="#34A853"
				d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
			/>
			<path
				fill="#FBBC05"
				d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
			/>
			<path
				fill="#EA4335"
				d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
			/>
		</svg>
	);
}

function GitHubIcon({ className }: { className?: string }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="currentColor">
			<path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v 3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
		</svg>
	);
}

const loginSchema = z.object({
	email: z.string().email({ message: "Invalid email address" }),
	password: z.string().min(8, {
		message: "Password must be at least 8 characters",
	}),
});

export default function Home() {
	const [isLoading, setIsLoading] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const { signIn } = useAuth();
	const { theme } = useTheme();
	const config = useAppConfig();

	const [isWebView] = useState(() => {
		if (typeof window === "undefined") {
			return false;
		}
		const ua = navigator.userAgent || "";
		const isIOS = /iPhone|iPad|iPod/i.test(ua);
		if (!isIOS) {
			return false;
		}
		return (
			/LinkedInApp|FBAN|FBAV|Instagram|Line\/|MicroMessenger|Twitter/i.test(
				ua,
			) ||
			(ua.includes("AppleWebKit") &&
				!ua.includes("Safari") &&
				!ua.includes("CriOS"))
		);
	});

	useUser({
		redirectTo: "/dashboard",
		redirectWhen: "authenticated",
		checkOnboarding: true,
	});

	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const error = params.get("error");
		if (error) {
			toast({
				title: decodeURIComponent(error),
				variant: "destructive",
			});
			window.history.replaceState({}, "", window.location.pathname);
		}
	}, []);

	const form = useForm<z.infer<typeof loginSchema>>({
		resolver: zodResolver(loginSchema),
		defaultValues: {
			email: "",
			password: "",
		},
	});

	useEffect(() => {
		if (window.PublicKeyCredential) {
			void signIn.passkey({ autoFill: true });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	async function onSubmit(values: z.infer<typeof loginSchema>) {
		setIsLoading(true);
		const { error } = await signIn.email(
			{
				email: values.email,
				password: values.password,
			},
			{
				onSuccess: () => {
					toast({ title: "Logged in successfully!" });
					window.location.href = "/dashboard";
				},
				onError: (ctx) => {
					toast({
						title: ctx.error.message || "Login failed",
						variant: "destructive",
					});
				},
			},
		);

		if (error) {
			toast({
				title: error.message || "An error occurred",
				variant: "destructive",
			});
		}

		setIsLoading(false);
	}

	async function handlePasskeySignIn() {
		setIsLoading(true);
		try {
			const res = await signIn.passkey();
			if (res?.error) {
				toast({
					title: res.error.message || "Failed to sign in with passkey",
					variant: "destructive",
				});
				return;
			}
			toast({ title: "Logged in successfully!" });
			window.location.href = "/dashboard";
		} catch (error: unknown) {
			toast({
				title: (error as Error)?.message || "Failed to sign in",
				variant: "destructive",
			});
		} finally {
			setIsLoading(false);
		}
	}

	return (
		<div className="flex min-h-screen w-screen overflow-hidden">
			{/* Left Panel */}
			<div className="hidden flex-1 flex-col items-center justify-center bg-muted p-12 lg:flex relative">
				<div className="text-center">
					<Image
						src={
							theme === "dark"
								? "/brand-mark-dark.png"
								: "/brand-mark-light.png"
						}
						alt="RouterBench"
						width={80}
						height={80}
						priority
						className="mx-auto mb-6 h-auto w-auto"
					/>
					<h2 className="text-xl font-bold text-foreground tracking-tight mb-2">
						RouterBench
					</h2>
					<p className="text-sm text-muted-foreground leading-relaxed max-w-[280px]">
						Unified LLM gateway for every provider. One API, every model, smart
						routing.
					</p>
				</div>

				<div className="absolute bottom-12 left-12 right-12 flex gap-8 border-t border-border pt-6">
					<div>
						<div className="text-lg font-bold text-foreground tracking-tight">
							Every
						</div>
						<div className="text-xs text-muted-foreground mt-0.5">AI model</div>
					</div>
					<div>
						<div className="text-lg font-bold text-foreground tracking-tight">
							1,000+
						</div>
						<div className="text-xs text-muted-foreground mt-0.5">
							Developers
						</div>
					</div>
					<div>
						<div className="text-lg font-bold text-foreground tracking-tight">
							99.9%
						</div>
						<div className="text-xs text-muted-foreground mt-0.5">Uptime</div>
					</div>
				</div>
			</div>

			{/* Right Panel */}
			<div className="flex flex-1 items-center justify-center bg-background p-6 sm:p-12">
				<div className="w-full max-w-[380px]">
					{/* Logo */}
					<div className="mb-11 flex items-center gap-3">
						<Image
							src={
								theme === "dark"
									? "/brand-mark-dark.png"
									: "/brand-mark-light.png"
							}
							alt="RouterBench"
							width={36}
							height={36}
							priority
							className="h-auto w-auto"
						/>
						<span className="text-lg font-bold text-foreground tracking-tight">
							RouterBench
						</span>
					</div>

					{/* Header */}
					<div className="mb-8">
						<h1 className="text-2xl font-bold text-foreground tracking-tight mb-1.5">
							Welcome
						</h1>
						<p className="text-sm text-muted-foreground">
							Sign in to your account
						</p>
					</div>

					{/* Login Form */}
					<Form {...form}>
						<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
							<FormField
								control={form.control}
								name="email"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-sm font-medium text-foreground">
											Email
										</FormLabel>
										<FormControl>
											<div className="relative">
												<Input
													placeholder="you@example.com"
													type="email"
													className="pr-10"
													{...field}
												/>
												<Mail className="absolute right-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
											</div>
										</FormControl>
										<FormMessage className="text-xs" />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="password"
								render={({ field }) => (
									<FormItem>
										<div className="flex items-center justify-between">
											<FormLabel className="text-sm font-medium text-foreground">
												Password
											</FormLabel>
											<Link
												href="/forgot-password"
												className="text-xs font-medium text-muted-foreground hover:text-foreground"
											>
												Forgot?
											</Link>
										</div>
										<FormControl>
											<div className="relative">
												<Input
													placeholder="••••••••"
													type={showPassword ? "text" : "password"}
													className="pr-10"
													{...field}
												/>
												<button
													type="button"
													onClick={() => setShowPassword(!showPassword)}
													className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
												>
													{showPassword ? (
														<EyeOff className="size-4" />
													) : (
														<Eye className="size-4" />
													)}
												</button>
											</div>
										</FormControl>
										<FormMessage className="text-xs" />
									</FormItem>
								)}
							/>

							<Button
								type="submit"
								disabled={isLoading}
								className="w-full mt-2"
								size="lg"
							>
								{isLoading ? (
									<>
										<Loader2 className="mr-2 size-4 animate-spin" />
										Signing in
									</>
								) : (
									"Continue"
								)}
							</Button>
						</form>
					</Form>

					{/* Divider */}
					<div className="relative my-6">
						<div className="absolute inset-0 flex items-center">
							<span className="w-full border-t border-border" />
						</div>
						<div className="relative flex justify-center text-xs">
							<span className="bg-background px-3 text-muted-foreground font-medium">
								Or continue with
							</span>
						</div>
					</div>

					{/* Consumer social (Google/GitHub) — B2C only; hidden in enterprise
					    where onboarding is SSO/invite-only. */}
					{config.publicSignupAllowed && (
						<div className="flex gap-2.5">
							{isWebView ? (
								<p className="w-full text-center text-xs text-muted-foreground py-2">
									To sign in with Google, open this page in your browser (tap ⋮
									or ⋯ and select &quot;Open in Browser&quot;)
								</p>
							) : (
								<Button
									onClick={async () => {
										setIsLoading(true);
										try {
											await signIn.social({
												provider: "google",
												callbackURL:
													location.protocol +
													"//" +
													location.host +
													"/dashboard",
												errorCallbackURL:
													location.protocol + "//" + location.host + "/",
											});
										} finally {
											setIsLoading(false);
										}
									}}
									variant="outline"
									disabled={isLoading}
									size="lg"
									className="flex-1"
								>
									{isLoading ? (
										<Loader2 className="size-4 animate-spin" />
									) : (
										<GoogleIcon className="size-4 mr-2" />
									)}
									Google
								</Button>
							)}

							<Button
								onClick={async () => {
									setIsLoading(true);
									try {
										await signIn.social({
											provider: "github",
											callbackURL:
												location.protocol + "//" + location.host + "/dashboard",
											errorCallbackURL:
												location.protocol + "//" + location.host + "/",
										});
									} finally {
										setIsLoading(false);
									}
								}}
								variant="outline"
								disabled={isLoading}
								size="lg"
								className="flex-1"
							>
								{isLoading ? (
									<Loader2 className="size-4 animate-spin" />
								) : (
									<GitHubIcon className="size-4 mr-2" />
								)}
								GitHub
							</Button>
						</div>
					)}

					{/* Enterprise SSO (OIDC/AD) — shown only when configured */}
					{config.ssoEnabled && (
						<Button
							onClick={async () => {
								setIsLoading(true);
								try {
									await signIn.oauth2({
										providerId: config.ssoProviderId,
										callbackURL:
											location.protocol + "//" + location.host + "/dashboard",
										errorCallbackURL:
											location.protocol + "//" + location.host + "/",
									});
								} finally {
									setIsLoading(false);
								}
							}}
							variant="outline"
							disabled={isLoading}
							size="lg"
							className="w-full mt-2.5"
						>
							{config.ssoLabel}
						</Button>
					)}

					{/* Passkey */}
					<Button
						onClick={handlePasskeySignIn}
						variant="outline"
						disabled={isLoading}
						size="lg"
						className="w-full mt-2.5"
					>
						{isLoading ? (
							<Loader2 className="size-4 animate-spin" />
						) : (
							<Key className="size-4 mr-2" />
						)}
						Passkey
					</Button>

					{/* Footer — hidden when public signup is disabled (enterprise) */}
					{config.publicSignupAllowed && (
						<p className="text-center text-sm text-muted-foreground mt-8">
							Don&apos;t have an account?{" "}
							<Link
								href="/signup"
								className="font-semibold text-foreground hover:underline"
							>
								Sign up
							</Link>
						</p>
					)}
				</div>
			</div>
		</div>
	);
}
